import SimplePage from "@/components/layout/SimplePage";
import { FormDownload } from "@/components/FormDownload";
import { useContent } from "@/hooks/useContent";

const HOLIDAYS = [
  { name: "Rosh Hashanah", desc: "The Jewish New Year. Evening service, morning service, and tashlich at the water. Tickets required - contact the office." },
  { name: "Yom Kippur", desc: "The Day of Atonement. Kol Nidre, morning service, afternoon study, Yizkor, and Ne'ilah. Tickets required." },
  { name: "Sukkot", desc: "The Feast of Booths. We build a sukkah on the temple grounds, shake the lulav and etrog, and gather for festive meals." },
  { name: "Simchat Torah", desc: "Rejoicing of the Torah. We dance with the Torah scrolls and read the end and the beginning of the Torah in one joyful service." },
  { name: "Hanukkah", desc: "The Festival of Lights. Community menorah lighting, latkes, songs, and a Hanukkah Shabbat service." },
  { name: "Tu BiShvat", desc: "The New Year of the Trees. An interactive seder honoring our connection to nature and the land of Israel." },
  { name: "Purim", desc: "The most joyful holiday on the calendar. Megillah reading with full costume and graggers, hamantashen, and a festive spirit." },
  { name: "Passover", desc: "TIOB hosts a community Passover seder in the social hall - one of the best-loved gatherings of the year. RSVP required." },
  { name: "Shavuot", desc: "Receiving Torah. Evening study, Yizkor, and traditional dairy foods." },
  { name: "Tisha B'Av", desc: "Remembering loss and rebuilding hope. An evening service with the chanting of Eicha (Lamentations)." },
];

export default function Holidays() {
  // Fully-editable page copy. When the admin has not written anything yet,
  // we fall back to the original hardcoded content below.
  const { body: adminBody, loading } = useContent("holidays.body", "");
  const hasAdminContent = !loading && !!(adminBody && adminBody.trim() && adminBody !== "<p></p>");

  return (
    <SimplePage
      title="Holidays"
      eyebrow="The Jewish Year"
      subtitle="From the sweetness of Rosh Hashanah to the joyful chaos of Purim - we celebrate every season."
      path="/worship/holidays"
      ctaLabel="See upcoming holiday events"
      ctaTo="/calendar/month"
    >
      {hasAdminContent ? (
        <div dangerouslySetInnerHTML={{ __html: adminBody }} />
      ) : (
        <>
          <p>
            The Jewish calendar is one of TIOB's greatest gifts. Each holiday is
            celebrated with our particular blend of tradition, music, and joy.
            Visitors are always welcome at our festival services and gatherings.
          </p>

          <h2>High Holy Days</h2>
          <p>
            Rosh Hashanah and Yom Kippur services are open to all members and to
            non-members who reserve tickets in advance. To purchase High Holy Day
            tickets or inquire about membership, please contact the office at{" "}
            <strong>(321) 631-9494</strong> or{" "}
            <a href="mailto:tioffice@tiofbrevard.com">tioffice@tiofbrevard.com</a>.
          </p>

          <a
            href="/hhd-schedule-2026.png"
            target="_blank"
            rel="noopener noreferrer"
            className="not-prose block my-6 rounded-2xl overflow-hidden border border-[var(--line)] shadow-lg hover:shadow-2xl transition-shadow"
            data-testid="holidays-hhd-image-link"
          >
            <img
              src="/hhd-schedule-2026.png"
              alt="High Holy Days 5787 Schedule of Services"
              className="w-full h-auto block"
              loading="lazy"
              data-testid="holidays-hhd-image"
            />
          </a>

          <FormDownload
            label="Tishrei Ticket Order Form"
            file="/forms/tishrei-form.pdf"
            description="Printable order form for High Holy Day tickets. Download, complete, and return to the temple office."
            className="not-prose my-6"
          />

          <h2>Holidays Throughout the Year</h2>
          <ul>
            {HOLIDAYS.map((h) => (
              <li key={h.name}>
                <strong>{h.name}.</strong> {h.desc}
              </li>
            ))}
          </ul>

          <p className="text-sm text-[var(--muted)] italic">
            For exact dates and service times each year, see our{" "}
            <a href="/calendar/month">temple calendar</a> or contact the temple office.
          </p>
        </>
      )}
    </SimplePage>
  );
}
